import type {
  ConsentManagerConfig,
  ConsentData,
  ConsentCategory,
  Translation,
} from '@/core/types';
import { ModalStyles } from './modal-styles';

export class ConsentModal {
  private element: HTMLElement | null = null;
  private shadowRoot: ShadowRoot | null = null;
  private config: ConsentManagerConfig;
  private translation: Translation;
  private currentConsent: ConsentData;
  private onSave: (consent: ConsentData) => void;
  private onClose: () => void;
  private isVisible = false;

  constructor(
    config: ConsentManagerConfig,
    translation: Translation,
    currentConsent: ConsentData,
    callbacks: {
      onSave: (consent: ConsentData) => void;
      onClose: () => void;
    }
  ) {
    this.config = config;
    this.translation = translation;
    this.currentConsent = { ...currentConsent };
    this.onSave = callbacks.onSave;
    this.onClose = callbacks.onClose;
  }

  public show(): void {
    if (this.isVisible) return;

    this.createElement();
    this.attachToDOM();
    this.isVisible = true;

    document.body.style.overflow = 'hidden';

    requestAnimationFrame(() => {
      this.element?.classList.add('cmp-modal--visible');
    });
  }

  public hide(): void {
    if (!this.isVisible || !this.element) return;

    this.element.classList.remove('cmp-modal--visible');
    document.body.style.overflow = '';

    setTimeout(() => {
      this.removeFromDOM();
      this.isVisible = false;
    }, 300);
  }

  public isShowing(): boolean {
    return this.isVisible;
  }

  private createElement(): void {
    this.element = document.createElement('div');
    this.element.className = 'cmp-modal';
    this.element.setAttribute('role', 'dialog');
    this.element.setAttribute('aria-label', this.translation.modal.title);
    this.element.setAttribute('aria-modal', 'true');

    this.shadowRoot = this.element.attachShadow({ mode: 'closed' });

    const styles = document.createElement('style');
    styles.textContent = ModalStyles.getStyles(this.config);

    const content = document.createElement('div');
    content.className = 'cmp-modal__backdrop';
    content.innerHTML = this.getHTML();

    this.shadowRoot.appendChild(styles);
    this.shadowRoot.appendChild(content);

    this.attachEventListeners();
  }

  private getHTML(): string {
    const categories = this.config.categories || this.getDefaultCategories();

    return `
      <div class="cmp-modal__container">
        <div class="cmp-modal__header">
          <h2 class="cmp-modal__title">${this.escapeHTML(this.translation.modal.title)}</h2>
          <button 
            type="button" 
            class="cmp-modal__close" 
            data-action="close"
            aria-label="Close modal"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path d="M15.898 4.102a.75.75 0 0 0-1.06 0L10 9.04 5.162 4.102a.75.75 0 1 0-1.06 1.06L9.04 10l-4.938 4.898a.75.75 0 1 0 1.06 1.06L10 10.96l4.898 4.938a.75.75 0 0 0 1.06-1.06L10.96 10l4.938-4.898a.75.75 0 0 0 0-1.06z"/>
            </svg>
          </button>
        </div>
        
        <div class="cmp-modal__body">
          <p class="cmp-modal__description">${this.escapeHTML(this.translation.modal.description)}</p>
          
          <div class="cmp-modal__categories">
            ${categories.map((category) => this.renderCategory(category)).join('')}
          </div>
        </div>
        
        <div class="cmp-modal__footer">
          <div class="cmp-modal__actions">
            <button 
              type="button" 
              class="cmp-modal__button cmp-modal__button--secondary" 
              data-action="reject-all"
            >
              ${this.escapeHTML(this.translation.modal.rejectAll)}
            </button>
            <button 
              type="button" 
              class="cmp-modal__button cmp-modal__button--secondary" 
              data-action="accept-all"
            >
              ${this.escapeHTML(this.translation.modal.acceptAll)}
            </button>
            <button 
              type="button" 
              class="cmp-modal__button cmp-modal__button--primary" 
              data-action="save"
            >
              ${this.escapeHTML(this.translation.modal.save)}
            </button>
          </div>
          <div class="cmp-modal__branding">
            Powered by <a href="https://cookievolve.com" target="_blank" rel="noopener">Cookievolve</a>
          </div>
        </div>
      </div>
    `;
  }

  private renderCategory(category: ConsentCategory): string {
    const isChecked = this.currentConsent[category.id] === true;
    const translatedCategory = this.translation.categories[category.id];

    return `
      <div class="cmp-modal__category">
        <div class="cmp-modal__category-header">
          <div class="cmp-modal__category-info">
            <h3 class="cmp-modal__category-name">${this.escapeHTML(translatedCategory.name)}</h3>
            <p class="cmp-modal__category-description">${this.escapeHTML(translatedCategory.description)}</p>
          </div>
          <div class="cmp-modal__toggle-wrapper">
            <label class="cmp-modal__toggle" for="consent-${category.id}">
              <input 
                type="checkbox" 
                id="consent-${category.id}"
                data-category="${category.id}"
                ${isChecked ? 'checked' : ''}
                ${category.required ? 'disabled' : ''}
                class="cmp-modal__toggle-input"
              />
              <span class="cmp-modal__toggle-slider"></span>
              <span class="sr-only">
                Toggle ${translatedCategory.name} cookies
              </span>
            </label>
            ${
              category.required
                ? `
              <span class="cmp-modal__required-badge">Required</span>
            `
                : ''
            }
          </div>
        </div>
        
        ${
          category.purposes && category.purposes.length > 0
            ? `
          <div class="cmp-modal__category-details">
            <h4 class="cmp-modal__purposes-title">Purposes:</h4>
            <ul class="cmp-modal__purposes-list">
              ${category.purposes
                .map(
                  (purpose) => `
                <li class="cmp-modal__purpose-item">${this.escapeHTML(purpose)}</li>
              `
                )
                .join('')}
            </ul>
          </div>
        `
            : ''
        }
        
        ${
          category.cookies && category.cookies.length > 0
            ? `
          <details class="cmp-modal__cookie-details">
            <summary class="cmp-modal__cookie-summary">View cookies (${category.cookies.length})</summary>
            <div class="cmp-modal__cookie-list">
              ${category.cookies
                .map(
                  (cookie) => `
                <div class="cmp-modal__cookie-item">${this.escapeHTML(cookie)}</div>
              `
                )
                .join('')}
            </div>
          </details>
        `
            : ''
        }
      </div>
    `;
  }

  private attachEventListeners(): void {
    if (!this.shadowRoot) return;

    const container = this.shadowRoot.querySelector('.cmp-modal__container');
    const backdrop = this.shadowRoot.querySelector('.cmp-modal__backdrop');

    if (!container || !backdrop) return;

    backdrop.addEventListener('click', (event) => {
      if (event.target === backdrop) {
        this.onClose();
      }
    });

    container.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      const actionElement = target.closest('[data-action]') as HTMLElement;
      const action = actionElement?.getAttribute('data-action');

      if (!action) return;

      event.preventDefault();
      event.stopPropagation();

      switch (action) {
        case 'save':
          this.handleSave();
          break;
        case 'accept-all':
          this.handleAcceptAll();
          break;
        case 'reject-all':
          this.handleRejectAll();
          break;
        case 'close':
          this.onClose();
          break;
      }
    });

    container.addEventListener('change', (event) => {
      const target = event.target as HTMLInputElement;
      if (target.type === 'checkbox' && target.hasAttribute('data-category')) {
        this.handleCategoryToggle(target);
      }
    });

    document.addEventListener('keydown', this.handleKeyDown.bind(this));
  }

  private handleCategoryToggle(input: HTMLInputElement): void {
    const category = input.getAttribute('data-category') as keyof ConsentData;
    if (
      category &&
      category !== 'timestamp' &&
      category !== 'version' &&
      category !== 'region' &&
      category !== 'language'
    ) {
      this.currentConsent[category] = input.checked;
    }
  }

  private handleSave(): void {
    this.onSave(this.currentConsent);
  }

  private handleAcceptAll(): void {
    const updatedConsent = {
      ...this.currentConsent,
      essential: true,
      analytics: true,
      advertising: true,
      functional: true,
      personalization: true,
    };
    this.onSave(updatedConsent);
  }

  private handleRejectAll(): void {
    const updatedConsent = {
      ...this.currentConsent,
      essential: true,
      analytics: false,
      advertising: false,
      functional: false,
      personalization: false,
    };
    this.onSave(updatedConsent);
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (!this.isVisible) return;

    if (event.key === 'Escape') {
      this.onClose();
      return;
    }

    if (event.key === 'Tab') {
      this.handleTabNavigation(event);
    }
  }

  private handleTabNavigation(event: KeyboardEvent): void {
    if (!this.shadowRoot) return;

    const focusableElements = this.shadowRoot.querySelectorAll(
      'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"]), details'
    );

    const firstFocusable = focusableElements[0] as HTMLElement;
    const lastFocusable = focusableElements[
      focusableElements.length - 1
    ] as HTMLElement;

    if (event.shiftKey) {
      if (document.activeElement === firstFocusable) {
        event.preventDefault();
        lastFocusable.focus();
      }
    } else {
      if (document.activeElement === lastFocusable) {
        event.preventDefault();
        firstFocusable.focus();
      }
    }
  }

  private attachToDOM(): void {
    if (!this.element) return;

    const existingModal = document.querySelector('.cmp-modal');
    if (existingModal) {
      existingModal.remove();
    }

    document.body.appendChild(this.element);

    setTimeout(() => {
      const firstButton = this.shadowRoot?.querySelector(
        'button'
      ) as HTMLElement;
      firstButton?.focus();
    }, 100);
  }

  private removeFromDOM(): void {
    if (this.element && this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
    this.element = null;
    this.shadowRoot = null;

    document.removeEventListener('keydown', this.handleKeyDown.bind(this));
  }

  private escapeHTML(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  private getDefaultCategories(): ConsentCategory[] {
    return [
      {
        id: 'essential',
        name: 'Essential',
        description: 'Required for basic site functionality',
        required: true,
        purposes: ['Authentication', 'Security', 'Load balancing'],
      },
      {
        id: 'analytics',
        name: 'Analytics',
        description: 'Help us understand how visitors use our site',
        required: false,
        purposes: ['Usage analytics', 'Performance monitoring'],
      },
      {
        id: 'advertising',
        name: 'Advertising',
        description: 'Used to deliver relevant advertisements',
        required: false,
        purposes: ['Targeted advertising', 'Ad measurement'],
      },
      {
        id: 'functional',
        name: 'Functional',
        description: 'Enable enhanced functionality and features',
        required: false,
        purposes: ['Chat widgets', 'Social media integration'],
      },
      {
        id: 'personalization',
        name: 'Personalization',
        description: 'Remember your preferences and settings',
        required: false,
        purposes: ['User preferences', 'Customized content'],
      },
    ];
  }

  public updateConsent(consent: ConsentData): void {
    this.currentConsent = { ...consent };
    if (this.isVisible && this.shadowRoot) {
      const checkboxes = this.shadowRoot.querySelectorAll(
        'input[type="checkbox"][data-category]'
      );
      checkboxes.forEach((checkbox) => {
        const input = checkbox as HTMLInputElement;
        const category = input.getAttribute(
          'data-category'
        ) as keyof ConsentData;
        if (
          category &&
          category !== 'timestamp' &&
          category !== 'version' &&
          category !== 'region' &&
          category !== 'language'
        ) {
          input.checked = this.currentConsent[category] === true;
        }
      });
    }
  }

  public destroy(): void {
    this.hide();
    document.removeEventListener('keydown', this.handleKeyDown.bind(this));
  }
}
